import { useAccount } from "@azure/msal-react";
import {
    ActionIcon,
    Alert,
    Anchor,
    Avatar,
    Badge,
    Box,
    Button,
    Flex,
    Group,
    Image,
    Menu,
    Modal,
    rem,
    Text,
    Title,
    useMantineColorScheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
    IconAlertCircle,
    IconDatabase,
    IconDotsVertical,
    IconFileDislike,
    IconFileLike,
    IconTrash,
} from "@tabler/icons-react";
import { useLiveQuery } from "dexie-react-hooks";
import "property-information";
import { useEffect, useState } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import type { PluggableList } from "unified";
import { db, type MessageEntity, type Reference } from "../db";
import { useLiveStreamedContent } from "../hooks/useChatCompletion";
import { useDatasources } from "../hooks/useDatasources";
import "../styles/markdown.scss";
import { parseAccountName } from "../utils/accountName";
import { processMarkdownContent } from "../utils/markdownProcessing";
import { AnswerWithSources } from "./AnswerWithSources";
import { CopyToClipboardButton } from "./CopyToClipboardButton";
import { DeleteMessageEntityItemModal } from "./DeleteChatMessageItemModal";
import { FollowUpSuggestions } from "./FollowUpSuggestions";
import { InlineReference } from "./InlineReference";
import { FeedbackType } from "./MessageUtils";
import { MDA, MDCode, MDTable } from "./markdown/MarkdownComponents";
import { NegativeFeedbackModal } from "./NegativeFeedbackModal";
import { PdfFocusProvider } from "./PDFViewer/context";
import { ReadingIndicator } from "./ReadingIndicator";
import { TimeseriesPlot, useIsChartReference } from "./TimeseriesPlot";

export interface InlineRefProps {
    node?: unknown;
    index: string;
    display?: string;
    [key: string]: unknown;
}

/**
 * Parse context from message content if present
 * Context format: [Context: Label1: Value1, Label2: Value2]\n\nActual message
 */
interface ParsedContextTag {
    label: string;
    value: string;
    type: "equipment" | "alert" | "location" | "default";
}

interface ParsedMessageContent {
    contextTags: ParsedContextTag[];
    actualContent: string;
}

function parseMessageContext(content: string): ParsedMessageContent {
    // Context is now at the END of the message (so previews show the question, not context)
    // Format: "User question\n\n[Context: Label: Value, Label2: Value2]"
    const contextMatch = content.match(/^([\s\S]*?)\n\n\[Context: ([^\]]+)\]$/);

    if (!contextMatch) {
        return { contextTags: [], actualContent: content };
    }

    const actualContent = contextMatch[1];
    const contextString = contextMatch[2];

    // Parse context tags (format: "Label: Value, Label2: Value2")
    const contextTags: ParsedContextTag[] = [];
    const tagParts = contextString.split(", ");

    for (const part of tagParts) {
        const colonIndex = part.indexOf(": ");
        if (colonIndex > 0) {
            const label = part.substring(0, colonIndex).trim();
            const value = part.substring(colonIndex + 2).trim();

            // Determine type based on label
            let type: ParsedContextTag["type"] = "default";
            if (label.toLowerCase() === "equipment") {
                type = "equipment";
            } else if (label.toLowerCase() === "alert") {
                type = "alert";
            } else if (label.toLowerCase() === "location") {
                type = "location";
            }

            contextTags.push({ label, value, type });
        }
    }

    return { contextTags, actualContent };
}

/**
 * Context pills component to display context tags above messages
 */
function ContextPills({ tags }: { tags: ParsedContextTag[] }) {
    if (tags.length === 0) return null;

    return (
        <Group gap="xs" mb="xs">
            {tags.map((tag) => (
                <Badge
                    key={`${tag.label}-${tag.value}`}
                    size="sm"
                    variant="light"
                    color={
                        tag.type === "equipment"
                            ? "purple"
                            : tag.type === "alert"
                              ? "red"
                              : tag.type === "location"
                                ? "blue"
                                : "gray"
                    }
                >
                    <Text size="xs" fw={500} span>
                        {tag.label}:
                    </Text>{" "}
                    {tag.value}
                </Badge>
            ))}
        </Group>
    );
}

interface AnswerProps {
    content: string;
    index: number;
    answers: string[];
    isAssistant: boolean;
    message: MessageEntity;
    question: MessageEntity | null;
    retry: (newMessage: string) => Promise<void>;
    nextAssistantMessage: MessageEntity | undefined;
    inProgress: boolean;
    allReferences?: Reference[];
}

export type ComponentsWithInlineRef = Components & {
    "inline-ref": React.ComponentType<InlineRefProps>;
};

export const isAllCaps = (str: string): boolean => str === str.toUpperCase();

const Answer = ({
    content,
    index,
    answers,
    isAssistant,
    message,
    question,
    retry,
    nextAssistantMessage,
    inProgress,
    allReferences,
}: AnswerProps) => {
    const isChartReference = useIsChartReference();
    return (
        <>
            {answers.length > 1 && content && isAssistant && !message.hasError ? (
                <Title order={6}>{index === 0 ? "CTS Knowledge Base" : "Public data"}</Title>
            ) : null}
            {index === 0 && message.hasError ? (
                <Alert
                    data-testid="error-alert"
                    mt="xs"
                    mb="md"
                    variant="light"
                    color="red"
                    title="Error happened"
                >
                    <Text mb="sm">
                        {(() => {
                            // Handle both string and object errors
                            const error = message.error;
                            if (!error) return "An error occurred";
                            if (typeof error === "string") {
                                return error.includes("Unauthorized") ? "An error occurred" : error;
                            }
                            // Handle object errors (e.g., validation errors from API)
                            if (typeof error === "object") {
                                const errorObj = error as {
                                    msg?: string;
                                    message?: string;
                                    detail?: string;
                                };
                                return (
                                    errorObj.msg ||
                                    errorObj.message ||
                                    errorObj.detail ||
                                    "An error occurred"
                                );
                            }
                            return "An error occurred";
                        })()}
                    </Text>
                    <Button
                        variant="filled"
                        c="white"
                        data-testid="retry-button"
                        size="xs"
                        onClick={() => {
                            // If question content is empty, find the last user message
                            if (!question?.content) {
                                if (!db) {
                                    // In test environments, db might not be available
                                    // Use a plain fallback instead of database access
                                    retry("Could you please try again?");
                                } else {
                                    // We need to get messages from the database to find the last user question
                                    db.messages
                                        .where("chatId")
                                        .equals(message.chatId)
                                        .sortBy("createdAt")
                                        .then((messages) => {
                                            // Find the last user message with content
                                            const lastUserMessage = [...messages]
                                                .reverse()
                                                .find((msg) => msg.role === "user" && msg.content);

                                            if (lastUserMessage?.content) {
                                                retry(decodeURIComponent(lastUserMessage.content));
                                            } else {
                                                // Fallback if no user message is found
                                                retry("Could you please try again?");
                                            }
                                        });
                                }
                            } else {
                                retry(decodeURIComponent(question.content));
                            }
                        }}
                    >
                        Try Again
                    </Button>
                </Alert>
            ) : (
                !message.done &&
                typeof nextAssistantMessage?.content === "undefined" &&
                !message.content &&
                !message.hasError &&
                null
            )}

            {isAssistant && allReferences && allReferences.length > 0 ? (
                <AnswerWithSources
                    answer={content}
                    references={allReferences}
                    error={!!message.error}
                    showDisclaimer={true}
                    header={
                        inProgress && index === 0 ? (
                            <ReadingIndicator toolActivities={message.toolActivities} />
                        ) : undefined
                    }
                >
                    {content && !inProgress && (
                        <Box ml="auto">
                            <CopyToClipboardButton content={content} size="xs" />
                        </Box>
                    )}
                </AnswerWithSources>
            ) : (
                <Box className="markdown" data-testid="markdown" pos="relative">
                    {isAssistant && content && !inProgress && (
                        <Box pos="absolute" top={0} right={0} style={{ zIndex: 10 }}>
                            <CopyToClipboardButton content={content} size="xs" />
                        </Box>
                    )}
                    {/* Keep the indicator on top for the whole stream (response_id → end /
                        error / close) — the model may emit partial text while still working. */}
                    {inProgress && index === 0 && (
                        <ReadingIndicator toolActivities={message.toolActivities} />
                    )}
                    {!message.content && inProgress
                        ? null
                        : !message.error && (
                              <PdfFocusProvider>
                                  <ReactMarkdown
                                      remarkPlugins={[remarkGfm]}
                                      rehypePlugins={[rehypeRaw] as PluggableList}
                                      components={
                                          {
                                              table: MDTable,
                                              code: MDCode,
                                              a: MDA,
                                              // Custom renderer for our inline <inline-ref> tag.
                                              "inline-ref": ({
                                                  node,
                                                  ...props
                                              }: InlineRefProps) => {
                                                  const { index, display } = props;
                                                  const refItem = allReferences?.find(
                                                      (ref) => ref.reference_id === index,
                                                  );

                                                  if (!refItem) {
                                                      return <span>[{display || index}]</span>;
                                                  }
                                                  if (isChartReference(refItem)) {
                                                      return (
                                                          <Box
                                                              component="span"
                                                              display="block"
                                                              my="sm"
                                                          >
                                                              <InlineReference
                                                                  reference={refItem}
                                                                  index={display || index}
                                                                  showDisclaimer={true}
                                                              />
                                                              <TimeseriesPlot reference={refItem} />
                                                          </Box>
                                                      );
                                                  }
                                                  return (
                                                      <InlineReference
                                                          reference={refItem}
                                                          index={display || index}
                                                          showDisclaimer={true}
                                                      />
                                                  );
                                              },
                                          } as ComponentsWithInlineRef
                                      }
                                  >
                                      {processMarkdownContent(content, allReferences)}
                                  </ReactMarkdown>
                              </PdfFocusProvider>
                          )}
                </Box>
            )}
        </>
    );
};

export const AnswersToBeRendered = ({
    answers,
    isAssistant,
    message,
    question,
    retry,
    nextAssistantMessage,
    inProgress,
    allReferences,
}: {
    answers: string[];
    isAssistant: boolean;
    message: MessageEntity;
    question: MessageEntity | null;
    retry: (newMessage: string) => Promise<void>;
    nextAssistantMessage: MessageEntity | undefined;
    inProgress: boolean;
    allReferences?: Reference[];
}) => {
    const renderAnswer = (content = "", index = 0) => (
        <Answer
            key={`answer-${message.responseId}-${message.id}`}
            content={content}
            index={index}
            answers={answers}
            isAssistant={isAssistant}
            message={message}
            question={question}
            retry={retry}
            nextAssistantMessage={nextAssistantMessage}
            inProgress={inProgress}
            allReferences={allReferences}
        />
    );

    return (
        <>
            {answers.length
                ? answers.map((content, index) => renderAnswer(content, index))
                : renderAnswer()}
        </>
    );
};

export const MessageItem = function MessageItem({
    isCompact,
    message,
    nextAssistantMessage,
    retry,
    sendFeedbackToApi,
    inProgress,
    followUpSuggestions,
    isLastMessage,
}: {
    sendFeedbackToApi: (
        apiKey: string | undefined,
        responseId: string,
        feedbackType: FeedbackType,
        feedback: string,
    ) => Promise<void>;
    message: MessageEntity;
    nextAssistantMessage?: MessageEntity;
    retry: (newMessage: string) => Promise<void>;
    inProgress: boolean;
    isCompact?: boolean;
    followUpSuggestions?: string[];
    isLastMessage?: boolean;
}) {
    const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);
    const [datasourcesOpened, { open: openDatasources, close: closeDatasources }] =
        useDisclosure(false);
    const { data: allDatasources } = useDatasources();
    const account = useAccount();
    const apiKey = account?.idToken;
    const { colorScheme } = useMantineColorScheme();
    const parsedAccountName = parseAccountName(account?.name);
    const userInitials = `${parsedAccountName?.first[0] ?? ""}${parsedAccountName?.last[0] ?? ""}`;

    useEffect(() => {
        document.querySelector(".mantine-AppShell-main")?.scrollIntoView(false);
    }, []);

    const question = useLiveQuery(async () => {
        if (!message.repliedId) {
            return null;
        }
        const result = await db.messages.get(message.repliedId);
        return result;
    }, [message.repliedId]);

    // Get all references from all messages in the same chatId.
    // Also depend on this message's references so plotly figures re-render when
    // a reference lands mid-stream on the current assistant message.
    const allReferences = useLiveQuery(async () => {
        const messages = await db.messages.where("chatId").equals(message.chatId).toArray();
        const references: Reference[] = [];
        for (const msg of messages) {
            if (msg.references) {
                references.push(...msg.references);
            }
        }
        return references;
    }, [message.chatId, message.references]);

    const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
    const [feedbackPlaceholder, setFeedbackPlaceholder] = useState<string | undefined>(undefined);
    const [feedbackType, setFeedbackType] = useState<FeedbackType>(FeedbackType.POSITIVE);

    const showOldword =
        message?.suggestions && message?.suggestions?.length > 0 && message.suggestions[0]?.oldWord;
    const isCaptialCase = showOldword && isAllCaps(message?.suggestions?.[0]?.oldWord ?? "");
    const isAssistant = message.role === "assistant";

    // Parse context from user messages to display as pills
    const isUserMessage = message.role === "user";
    const parsedContent =
        isUserMessage && message?.content
            ? parseMessageContext(message.content)
            : { contextTags: [], actualContent: message?.content ?? "" };

    // While an assistant answer streams, its text lives in an in-memory store (IndexedDB is
    // written just once at the end). Overlay that live text so the answer renders as it
    // arrives without the DB churn.
    const liveStreamed = useLiveStreamedContent(
        isAssistant ? String(message.id) : undefined,
        message?.content ?? "",
    );
    const assistantContent = liveStreamed ?? message?.content ?? "";

    // Use actual content (without context prefix) for display
    const displayContent = isUserMessage ? parsedContent.actualContent : assistantContent;
    const answers = [displayContent, message?.pretrained].filter((c) => Boolean(c) && c !== "N/A");

    // Answer inspects `message.content` directly for its "still loading" checks — give it the
    // live text so it renders streaming output instead of the reading indicator.
    const messageForAnswer =
        liveStreamed != null
            ? ({ ...message, content: assistantContent } as MessageEntity)
            : message;

    const [isSendingFeedback, setIsSendingFeedback] = useState(false);
    const sendFeedback = async (
        feedbackType: FeedbackType,
        allowFeedbackComment: boolean,
        feedbackPlaceholder?: string,
    ) => {
        if (!isSendingFeedback) {
            setIsSendingFeedback(true);
            setFeedbackType(feedbackType);

            if (allowFeedbackComment) {
                setFeedbackPlaceholder(feedbackPlaceholder);
                openModal();
            } else {
                await sendFeedbackToApi(apiKey, message.responseId, feedbackType, "");
            }
            setIsSendingFeedback(false);
        }
    };

    return (
        <>
            {showOldword ? (
                <Alert
                    style={(theme) => ({
                        backgroundColor: colorScheme === "dark" ? theme.colors.dark[6] : "#fff",
                    })}
                    icon={<IconAlertCircle size="1rem" />}
                    title={
                        <Anchor
                            onClick={() => {
                                retry(
                                    decodeURIComponent(question?.content ?? "").replace(
                                        message?.suggestions?.[0]?.oldWord ?? "",
                                        message?.suggestions?.[0]?.text ?? "",
                                    ),
                                );
                            }}
                        >
                            <Title display="flex" order={6} c="blue">
                                <Text fw="400">Search for </Text>{" "}
                                <Text
                                    mx=".25rem"
                                    style={{ textTransform: isCaptialCase ? "uppercase" : "none" }}
                                >
                                    {message.suggestions?.[0].text}
                                </Text>
                                instead of
                                <Text mx=".25rem">{message.suggestions?.[0].oldWord}</Text> ?
                            </Title>
                        </Anchor>
                    }
                    radius="xs"
                    withCloseButton
                    data-testid="error-alert"
                >
                    {""}
                </Alert>
            ) : (
                <Flex
                    direction="column"
                    gap="sm"
                    className="message-item"
                    style={(theme) => ({
                        backgroundColor: colorScheme === "dark" ? theme.colors.dark[6] : "#fff",
                        padding: theme.spacing.sm,
                        borderRadius: theme.radius.sm,
                        width: "100%",
                        marginBottom: "12px",
                    })}
                >
                    <Flex gap="sm">
                        {message.role === "user" && (
                            <Avatar color="blue" radius="xl" data-testid="user-avatar">
                                <Text c="black">{userInitials}</Text>
                            </Avatar>
                        )}
                        {isAssistant && (
                            <div data-testid="assistant-logo">
                                <Image
                                    fit="contain"
                                    src="/ai-icon.png"
                                    alt="AI Assistant"
                                    width={32}
                                    height={32}
                                />
                            </div>
                        )}

                        <Box
                            style={{ flex: 1, alignSelf: "center", position: "relative" }}
                            className="markdown"
                            data-testid="message-content"
                        >
                            {/* Show context pills for user messages with context */}
                            {isUserMessage && parsedContent.contextTags.length > 0 && (
                                <ContextPills tags={parsedContent.contextTags} />
                            )}
                            {!showOldword ? (
                                <AnswersToBeRendered
                                    answers={answers}
                                    isAssistant={isAssistant}
                                    message={messageForAnswer}
                                    question={question ?? null}
                                    retry={retry}
                                    nextAssistantMessage={nextAssistantMessage}
                                    inProgress={inProgress}
                                    allReferences={allReferences}
                                />
                            ) : null}
                        </Box>
                        {isAssistant ? (
                            <Menu
                                position="left"
                                shadow="md"
                                width={200}
                                data-testid="assistant-menu"
                            >
                                <Menu.Target>
                                    <ActionIcon
                                        aria-label="Actions"
                                        data-testid="assistant-menu-target"
                                    >
                                        <IconDotsVertical size={18} />
                                    </ActionIcon>
                                </Menu.Target>

                                <Menu.Dropdown data-testid="assistant-menu-dropdown">
                                    {!inProgress && (
                                        <>
                                            <Menu.Item
                                                leftSection={<IconFileLike size={14} />}
                                                onClick={() =>
                                                    sendFeedback(FeedbackType.POSITIVE, false)
                                                }
                                                data-testid="inside-like-menu-item"
                                            >
                                                Like
                                            </Menu.Item>
                                            <Menu.Item
                                                leftSection={<IconFileDislike size={14} />}
                                                onClick={() =>
                                                    sendFeedback(
                                                        FeedbackType.NEGATIVE,
                                                        true,
                                                        "This didn't answer my question, I expected …",
                                                    )
                                                }
                                                data-testid="inside-dislike-menu-item"
                                            >
                                                Dislike
                                            </Menu.Item>
                                        </>
                                    )}
                                    <Menu.Item
                                        leftSection={<IconDatabase size={14} />}
                                        data-testid="datasources-menu-item"
                                        onClick={openDatasources}
                                    >
                                        Datasources
                                    </Menu.Item>
                                    <Menu.Item
                                        color="red"
                                        leftSection={<IconTrash size={14} />}
                                        data-testid="delete-menu-item"
                                        onClick={openDelete}
                                    >
                                        Delete
                                    </Menu.Item>
                                </Menu.Dropdown>
                            </Menu>
                        ) : null}
                    </Flex>
                    {isAssistant && !inProgress && (
                        <Group style={{ justifyContent: "end" }}>
                            <Text>Did we answer your question?</Text>
                            <ActionIcon
                                color="green"
                                variant="subtle"
                                size="lg"
                                aria-label="Like"
                                style={{ cursor: "pointer" }}
                                onClick={() => sendFeedback(FeedbackType.POSITIVE, false)}
                                data-testid="like-menu-item"
                            >
                                <IconFileLike style={{ width: rem(20) }} stroke={1.5} />
                            </ActionIcon>
                            <ActionIcon
                                variant="subtle"
                                color="red"
                                size="lg"
                                aria-label="Dislike"
                                style={{ cursor: "pointer" }}
                                onClick={() =>
                                    sendFeedback(
                                        FeedbackType.NEGATIVE,
                                        true,
                                        "This didn't answer my question, I expected …",
                                    )
                                }
                                data-testid="dislike-menu-item"
                            >
                                <IconFileDislike style={{ width: rem(20) }} stroke={1.5} />
                            </ActionIcon>
                        </Group>
                    )}
                    {/* Follow-up suggestions */}
                    {isAssistant && !inProgress && isLastMessage && (
                        <FollowUpSuggestions
                            suggestions={followUpSuggestions || []}
                            onSuggestionClick={retry}
                            maxSuggestions={isCompact ? 1 : undefined}
                            compact={isCompact}
                        />
                    )}
                </Flex>
            )}

            <Modal
                opened={datasourcesOpened}
                onClose={closeDatasources}
                title="Datasources"
                data-testid="datasources-modal"
            >
                {message.datasources && message.datasources.length > 0 ? (
                    <Group gap="xs">
                        {message.datasources.map((id) => {
                            const match = allDatasources?.find((d) => d.datasource_id === id);
                            return (
                                <Badge key={id} variant="light">
                                    {match?.datasource_label ?? id}
                                </Badge>
                            );
                        })}
                    </Group>
                ) : (
                    <Text size="sm" c="dimmed">
                        No datasources available.
                    </Text>
                )}
            </Modal>
            <DeleteMessageEntityItemModal
                message={message}
                isOpen={deleteOpened}
                close={closeDelete}
            />
            <NegativeFeedbackModal
                opened={modalOpened}
                onClose={closeModal}
                placeholder={feedbackPlaceholder}
                feedbackType={feedbackType}
                onSubmit={async (type, feedback) => {
                    await sendFeedbackToApi(apiKey, message.responseId, type, feedback);
                }}
            />
        </>
    );
};
